import {
  ActionIcon,
  Box,
  Button,
  Group,
  ScrollArea,
  Skeleton,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconPlus, IconX } from "@tabler/icons-react";
import SelectComp from "../../../commonComponents/components/select/SelectComp";
import TextInputComp from "../../../commonComponents/components/textInput/TextInputComp";
import { memo, useEffect, useState } from "react";
import SelectWithComboAPI from "../../../commonComponents/components/selectWithAPI/SelectWithComboAPI";

const consumed_by = ["A", "C", "D", "F"]
  ?.map((item) => {
    return Array(18)
      .fill(0)
      ?.map((_, index) => {
        return `${index + 1}${item}`;
      });
  })
  .flat();
const CustomerSalesBody = ({
  allProducts,
  allPaymentReason,
  setCustomerSalesData,
  legProductsQuery,
  customerSalesData,
  isReadOnly,
  error,
  item,
  index,
  getComboDetailsQuery,
}) => {
  const [foodItems, setFoodItems] = useState([]);
  const [beverageItems, setBeverageItems] = useState([]);

  const updateRow = (index, updatedRow) => {
    setCustomerSalesData(prev =>
      prev.map((row, i) => (i === index ? updatedRow : row))
    );
  };

  const handleChange = (field, val) => {
    updateRow(index, {
      ...item,
      [field]: val,
    });
  };

  useEffect(() => {
    const rawFoodIds = item?.food_ids || [];
    const rawBeverageIds = item?.beverage_ids || [];
    const allSelectedIds = [...rawFoodIds, ...rawBeverageIds];

    const comboFood = item?.combo_details?.food || [];
    const comboBeverage = item?.combo_details?.beverage || [];

    let finalFood = [];
    let finalBeverage = [];

    allSelectedIds.forEach((pid) => {
      if (comboFood.includes(pid)) {
        finalFood.push(pid);
      } else if (comboBeverage.includes(pid)) {
        finalBeverage.push(pid);
      } else {
        console.warn("⚠️ Product not in combo:", pid);
      }
    });

    // keep UI state in sync
    setFoodItems(finalFood);
    setBeverageItems(finalBeverage);

  }, [item]);

  const comboId =
    item?.journey_order_id?.combo_id ??
    item?.journey_order_id?.journey_order_item?.[0]?.combo_id ??
    null;

  useEffect(() => {
    if (!comboId) return;
    if (item?.combo_details) return; // already hydrated

    const combo =
      getComboDetailsQuery?.find(
        c => Number(c.id) === Number(comboId)
      );

    if (!combo) return;

    updateRow(index, {
      ...item,
      combo_details: combo,
    });
  }, [comboId, getComboDetailsQuery]);

  const foodOptions =
    allProducts?.data?.filter(p =>
      item?.combo_details?.food?.includes(p.id)
    ) || [];

  const beverageOptions =
    allProducts?.data?.filter(p =>
      item?.combo_details?.beverage?.includes(p.id)
    ) || [];

  const getMaxComboQuantity = () => {
    const soldBy = item?.journey_order_id?.sold_by;
    if (!soldBy) return 0;

    const productIds = [
      ...(item?.food_ids || []),
      ...(item?.beverage_ids || [])
    ];

    if (!productIds.length) return 0;

    const remainingQuantities = productIds.map(productId => {
      const product = legProductsQuery?.data?.find(
        p => p?.product_id?.id === productId
      );
      if (!product) return 0;

      const soldQty = customerSalesData
        .filter(
          (row, rowIndex) =>
            rowIndex !== index &&
            row?.journey_order_id?.sold_by === soldBy &&
            (
              row?.food_ids?.includes(productId) ||
              row?.beverage_ids?.includes(productId)
            )
        )
        .reduce((sum, row) => sum + Number(row.quantity || 0), 0);

      const opening =
        soldBy === 'SCC'
          ? Number(product.opening_scc || 0)
          : Number(product.opening_cc || 0);

      return Math.max(opening - soldQty, 0);
    });

    // 🔥 combo quantity is MIN of all products
    return Math.min(...remainingQuantities);
  };

  const maxQty = getMaxComboQuantity();

  const getComboPriceById = () => {
    const comboId =
      item?.journey_order_id?.combo_id ||
      item?.journey_order_id?.journey_order_item?.[0]?.combo_id;

    if (!comboId) return 0;

    const combo = getComboDetailsQuery?.find(
      c => Number(c.id) === Number(comboId)
    );

    return Number(combo?.combo_price || 0);
  };

  const comboPrice = getComboPriceById();

  const sellPrice =
    Number(item?.quantity || 0) * comboPrice;

  useEffect(() => {
    handleChange("sell_price", sellPrice);
  }, [item?.quantity, comboPrice]);

  const comboItems =
    item?.journey_order_id?.journey_order_item || [];

  const isFOC = item?.journey_order_id?.sales_type === "FOC";

  const focReason =
    item?.journey_order_id?.payment_exception_reasons ||
    comboItems?.[0]?.payment_exception_reasons ||
    null;

  return (
    <Table.Tr
      onClick={() => {
        if (isReadOnly) {
          notifications.show({
            title: "Warning",
            message:
              "There are some leg changes not saved. Please save it or reset it.",
            color: "red",
            icon: <IconAlertCircle />,
          });
        }
      }}
      style={{ background: item?.quantity == 0 ? "#f88" : "" }}
    >
      <Table.Td>{parseInt(index) + 1}</Table.Td>
      <Table.Td>
        <SelectComp
          options={[
            { label: "SCC", value: "SCC" },
            { label: "CC", value: "CC" },
          ]}
          value={
            item?.journey_order_id?.sold_by?.toUpperCase() ||
            item?.journey_order?.sold_by
          }
          setValue={(e) => {
            updateRow(index, {
              ...item,
              quantity: 0,
              journey_order_id: {
                ...item.journey_order_id,
                sold_by: e,
              },
            });
          }}
          error={error?.sold_by}
          readOnly={isReadOnly}
        />
      </Table.Td>
      <Table.Td>
        <SelectWithComboAPI
          value={comboId || null}
          tableName="combo_products"
          itemData={{
            display_options: '["combo_name"]',
            return_value: "id",
            filter_empty_quantity: false,
          }}
          setValue={(val) => {
            if (!val) {
              updateRow(index, {
                ...item,
                journey_order_id: {
                  ...item.journey_order_id,
                  combo_id: null,
                  journey_order_item: [],   // 🔥 reset old combo items
                },
                food_ids: [],
                beverage_ids: [],
                combo_details: null,
              });
              return;
            }

            updateRow(index, {
              ...item,
              journey_order_id: {
                ...item.journey_order_id,
                combo_id: Number(val),
                journey_order_item: [],   // 🔥 VERY IMPORTANT
              },
              food_ids: [],
              beverage_ids: [],
              combo_details: null,
              quantity: 0,
            });
          }}
          onItemSelect={(selectedItem) => {
            if (!selectedItem) return;

            const combo =
              getComboDetailsQuery?.find(
                c => Number(c.id) === Number(selectedItem.value)
              ) || null;

            updateRow(index, {
              ...item,
              quantity: 0,
              combo_details: combo,
              journey_order_id: {
                ...item.journey_order_id,
                combo_id: Number(selectedItem.value),
                journey_order_item: [], // 🔥 RESET OLD COMBO ROWS
              },
              food_ids: [],
              beverage_ids: []
            });

            setFoodItems(selectedItem.food || []);
            setBeverageItems(selectedItem.beverage || []);
          }}
        />
      </Table.Td>

      <Table.Td>
        <SelectComp
          options={["Sales", "FOC"]}
          value={item?.journey_order_id?.sales_type || "Sales"}
          setValue={(e) => {
            const isNowFOC = e === "FOC";

            const existingItems =
              item?.journey_order_id?.journey_order_item || [];

            const newItems =
              existingItems.length > 0
                ? existingItems.map(ci => ({
                  ...ci,
                  sale_type: isNowFOC ? "FOC" : "Sales",
                  payment_exception_reasons: isNowFOC
                    ? ci.payment_exception_reasons
                    : null,
                }))
                : (item.food_ids || [])
                  .concat(item.beverage_ids || [])
                  .map(pid => ({
                    product_id: pid,
                    sale_type: isNowFOC ? "FOC" : "Sales",
                    payment_exception_reasons: null,
                  }));

            updateRow(index, {
              ...item,
              journey_order_id: {
                ...item.journey_order_id,
                sales_type: e,
                payment_exception_reasons: isNowFOC
                  ? item?.journey_order_id?.payment_exception_reasons || null
                  : null, // ✅ clear when Sales
                journey_order_item: newItems,
              },
            });
          }}
          readOnly={isReadOnly}
        />
      </Table.Td>
      <Table.Td>
        <SelectComp
          options={
            allPaymentReason?.data?.filter(
              r => !r?.for_internal_employees
            ) || []
          }
          value={focReason}
          disabled={!isFOC}
          readOnly={isReadOnly || !isFOC}
          setValue={(reason) => {
            let newItems = comboItems;

            if (!newItems || newItems.length === 0) {
              newItems = [...(item.food_ids || []), ...(item.beverage_ids || [])].map(
                (pid) => ({
                  product_id: pid,
                  sale_type: "FOC",
                  payment_exception_reasons: reason,
                })
              );
            } else {
              newItems = newItems.map((ci) => ({
                ...ci,
                payment_exception_reasons: reason,
                sale_type: "FOC",
              }));
            }

            updateRow(index, {
              ...item,
              journey_order_id: {
                ...item.journey_order_id,
                sales_type: "FOC",
                payment_exception_reasons: reason, // ✅ ADD HERE
                journey_order_item: newItems,
              },
            });
          }}
        />
      </Table.Td>
      <Table.Td>
        <SelectComp
          value={item?.journey_order_id?.seat || item?.journey_order?.seat}
          options={consumed_by}
          setValue={(e) => {
            updateRow(index, {
              ...item,
              journey_order_id: {
                ...item.journey_order_id,
                seat: e,
              },
            });
          }}
          error={error?.seat}
          readOnly={isReadOnly}
        />
      </Table.Td>
      <Table.Td>
        {/* Food */}
        <SelectComp
          data={foodOptions}
          value={foodItems?.[0]?.toString() || null}
          setValue={(e, option) => {
            if (!e || !option) {
              updateRow(index, {
                ...item,
                food_ids: [],
                journey_order_id: {
                  ...item.journey_order_id,
                  food_product_name: null,
                  food_product_code: null,
                  food_selling_price: 0,
                },
              });
              return;
            }

            updateRow(index, {
              ...item,
              food_ids: [option.id],
              journey_order_id: {
                ...item.journey_order_id,
                food_product_name: option.product_name,
                food_product_code: option.product_code,
                food_selling_price: option.selling_price,
              },
            });
          }}

          clearable   // ✅ important
          readOnly={isReadOnly}
        />
      </Table.Td>

      {/* ==================== Beverage ==================== */}

      <Table.Td>
        <SelectComp
          data={beverageOptions}
          value={beverageItems?.[0]?.toString() || null}
          setValue={(e, option) => {
            if (!e || !option) {
              updateRow(index, {
                ...item,
                beverage_ids: [],
                journey_order_id: {
                  ...item.journey_order_id,
                  beverage_product_name: null,
                  beverage_product_code: null,
                  beverage_selling_price: 0,
                },
              });
              return;
            }

            updateRow(index, {
              ...item,
              beverage_ids: [option.id],
              journey_order_id: {
                ...item.journey_order_id,
                beverage_product_name: option.product_name,
                beverage_product_code: option.product_code,
                beverage_selling_price: option.selling_price,
              },
            });
          }}

          clearable
          readOnly={isReadOnly}
        />
      </Table.Td>

      <Table.Td>
        <TextInputComp
          type="number"
          max={maxQty}
          value={item?.quantity ?? ''}
          onChange={(e) => {
            const rawValue = e.target.value;

            // ✅ allow clearing
            if (rawValue === '') {
              handleChange('quantity', '');
              return;
            }

            const enteredQty = Number(rawValue);

            // ⛔ invalid number
            if (Number.isNaN(enteredQty)) return;

            // ⛔ exceed max
            if (enteredQty > maxQty) {
              notifications.show({
                title: 'Quantity exceeded',
                message: `Maximum allowed quantity is ${maxQty}`,
                color: 'red',
                icon: <IconAlertCircle />,
              });
              return;
            }

            handleChange('quantity', enteredQty);
          }}
          error={error?.quantity}
        />
      </Table.Td>
      <Table.Td>
        <TextInputComp
          leftSection="₹"
          value={sellPrice ? sellPrice : 0}
          readOnly={isReadOnly}
        />
      </Table.Td>

      <Table.Td>
        <TextInputComp
          value={
            item?.journey_order_id?.invoice_email ||
            item?.journey_order?.invoice_email ||
            ""
          }
          setValue={(e) => {
            updateRow(index, {
              ...item,
              journey_order_id: {
                ...item.journey_order_id,
                invoice_email: e,
              },
            });
          }}
          placeholder={"Email"}
          readOnly={isReadOnly}
          error={error?.invoice_email}
        />
      </Table.Td>

      <Table.Td>
        {item?.is_new ? (
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={() => {
              setCustomerSalesData(prev =>
                prev.filter((_, i) => i !== index)
              );
            }}
          >
            <IconX size={18} />
          </ActionIcon>
        ) : null}
      </Table.Td>
    </Table.Tr>
  );
};

// eslint-disable-next-line react/display-name
const ComboOrders = memo(
  ({
    allProducts,
    allPaymentReason,
    customerSalesData,
    activeLeg,
    setCustomerSalesData,
    errorsObj,
    legProductsQuery,
    getCustomerSalesDetailsQuery,
    addNewCustomerSales,
    isReadOnly,
    getComboDetailsQuery,
  }) => {
    return (
      <Box>
        <ScrollArea type="auto" scrollbarSize={4}>
          <Table
            verticalSpacing={8}
            horizontalSpacing={4}
            miw={1200}
            tableLayout="fixed"
          >
            <Table.Thead>
              <Table.Tr c="gray.6">
                <Table.Td w={50}>#</Table.Td>
                <Table.Td w={100}>SCC/CC</Table.Td>
                <Table.Td w={160}>Combo Name</Table.Td>
                <Table.Td w={120}>Sales/FOC</Table.Td>
                <Table.Td w={180}>Reason for FOC</Table.Td>
                <Table.Td w={130}>Consumed By</Table.Td>
                <Table.Td w={160}>Product 1</Table.Td>
                <Table.Td w={160}>Product 2</Table.Td>
                <Table.Td w={80}>QTY</Table.Td>
                <Table.Td w={120}>Price</Table.Td>
                <Table.Td w={200}>Invoice</Table.Td>
                <Table.Td w={60}></Table.Td>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {!getCustomerSalesDetailsQuery?.isLoading &&
                customerSalesData?.length > 0 ? (
                customerSalesData?.map((item, index) => {
                  return (
                    <CustomerSalesBody
                      allProducts={allProducts}
                      allPaymentReason={allPaymentReason}
                      legProductsQuery={legProductsQuery}
                      item={item}
                      index={index}
                      error={errorsObj?.[index]}
                      customerSalesData={customerSalesData}
                      setCustomerSalesData={setCustomerSalesData}
                      isReadOnly={isReadOnly}
                      key={index}
                      getComboDetailsQuery={getComboDetailsQuery}
                    />
                  );
                })
              ) : getCustomerSalesDetailsQuery?.isLoading ||
                getCustomerSalesDetailsQuery?.isPending ? (
                <Table.Tr>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={40} w={"100%"} />
                  </Table.Td>
                </Table.Tr>
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={11} align="center">
                    <Text c={"dimmed"}>No records found</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        {!["cancelled", "auto cancelled"]?.includes(activeLeg?.status) ? (
          <Group justify="center" mt={"md"}>
            <Button
              size="xs"
              leftSection={<IconPlus size={16} />}
              variant="light"
              onClick={addNewCustomerSales}
            >
              Add New
            </Button>
          </Group>
        ) : null}
      </Box>
    );
  }
);

export default ComboOrders;
